import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Spinner } from 'react-bootstrap';
import { LuPencilLine } from "react-icons/lu";
import { IoTrashOutline } from "react-icons/io5";
import { urlApiProd } from '../utiles';

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

function EditarNotaFiscal({ notaFiscal, cerrar, guardar, proveedores }) {
    const [botonDeshabilitado, setBotonDeshabilitado] = useState(false);
    const [nroNF, setNroNF] = useState(notaFiscal.nro_nf || '');
    const [tipoNF, setTipoNF] = useState(notaFiscal.tipo_nf || '');
    const [llave, setLlave] = useState(notaFiscal.llave || '');
    const [proveedor, setProveedor] = useState(notaFiscal.id_proveedor || '');
    const [emision, setEmision] = useState(formatDate(notaFiscal.emision));
    const [salida, setSalida] = useState(formatDate(notaFiscal.salida));
    const [cnpj, setCnpj] = useState(notaFiscal.cnpj || '');
    const [valorProductos, setValorProductos] = useState(notaFiscal.valor_productos || 0);
    const [valorTotal, setValorTotal] = useState(notaFiscal.valor_total_nf ?? notaFiscal.valor_total ?? 0);
    const [pkg, setPkg] = useState(notaFiscal.pkg || '');
    const [descuento, setDescuento] = useState(notaFiscal.descuento || '');
    const [booking, setBooking] = useState(notaFiscal.booking || '');
    const [oc, setOc] = useState(notaFiscal.oc || '');
    const [detalles, setDetalles] = useState([]);
    const [loadingDetalles, setLoadingDetalles] = useState(true);
    const [availableGrades, setAvailableGrades] = useState([]);
    const [productNames, setProductNames] = useState([]);

    const handleSubmit = (event) => {
        event.preventDefault();
        setBotonDeshabilitado(true);

        const editedNotaFiscal = {
            id_nota_fiscal: notaFiscal.id_nota_fiscal,
            nro_nf: nroNF,
            tipo_nf: tipoNF,
            llave,
            id_proveedor: proveedor,
            emision,
            salida,
            cnpj,
            valor_productos: parseFloat(valorProductos) || 0,
            valor_total: parseFloat(valorTotal) || 0,
            pkg,
            descuento,
            booking,
            oc,
            detalles: detalles.map(d => ({
                id_detalle: d.id_detalle,
                id_nota_fiscal: d.id_nota_fiscal,
                id_producto: d.id_producto,
                producto: d.producto,
                calidad: d.calidad,
                medida: d.medida,
                ncm: d.ncm,
                m3: parseFloat(d.m3) || 0,
                usd_m3: parseFloat(d.usd_m3) || 0,
                total: parseFloat(d.total) || 0,
                descuento: d.descuento
            }))
        };

        guardar(editedNotaFiscal);
        // El cierre lo maneja el padre tras el éxito, pero reseteamos el botón por si falla
        setTimeout(() => setBotonDeshabilitado(false), 3000);
    };

    useEffect(() => {
        if (notaFiscal && notaFiscal.id_nota_fiscal && loadingDetalles) {
            fetchDetalles(notaFiscal.id_nota_fiscal);
        }
    }, [notaFiscal]);

    const fetchGrades = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${urlApiProd}/opciones`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.calidades) {
                    setAvailableGrades(data.calidades);
                }
            }
        } catch (error) {
            console.error('Error fetching grades:', error);
        }
    };

    const fetchDetalles = async (idNotaFiscal) => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${urlApiProd}/notasfiscales/${idNotaFiscal}/detalles`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await response.json();
            setDetalles(data);
            setLoadingDetalles(false);
        } catch (error) {
            console.error('Error al obtener detalles:', error);
            setLoadingDetalles(false);
        }
    };

    const fetchProductNames = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${urlApiProd}/nombreproductos`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                const uniqueProducts = [];
                const seenNames = new Set();
                data.forEach(item => {
                    const name = item.nombre_producto?.trim();
                    if (name && !seenNames.has(name)) {
                        seenNames.add(name);
                        uniqueProducts.push(item);
                    }
                });
                setProductNames(uniqueProducts);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    useEffect(() => {
        fetchGrades();
        fetchProductNames();
    }, []);

    const handleDetalleChange = (index, field, value) => {
        const updatedDetalles = [...detalles];
        updatedDetalles[index] = { ...updatedDetalles[index], [field]: value };

        // Auto-calcular total si cambian m3 o usd_m3
        if (field === 'm3' || field === 'usd_m3') {
            const m3 = parseFloat(field === 'm3' ? value : updatedDetalles[index].m3) || 0;
            const usd = parseFloat(field === 'usd_m3' ? value : updatedDetalles[index].usd_m3) || 0;
            updatedDetalles[index].total = (m3 * usd).toFixed(2);
        }

        setDetalles(updatedDetalles);
    };

    const handleAgregarDetalle = () => {
        setDetalles([...detalles, {
            producto: '', calidad: '', medida: '', ncm: '', m3: 0, usd_m3: 0, total: 0, descuento: '0'
        }]);
    };

    const handleDeleteDetalle = (index) => {
        const updatedDetalles = detalles.filter((_, i) => i !== index);
        setDetalles(updatedDetalles);
    };

    return (
        <div className='modal-overlay'>
            <div className='modal1'>
                <div className='card'>
                    <form onSubmit={handleSubmit}>
                        <div className='card-header'>
                            <h3 className='card-title' style={{ textAlign: 'center' }}>Editar Nota Fiscal</h3>
                        </div>
                        <div className='card-body'>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Número NF</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={nroNF}
                                    onChange={(e) => setNroNF(e.target.value)}
                                    required
                                />
                            </div>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Tipo NF</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={tipoNF}
                                    onChange={(e) => setTipoNF(e.target.value)}
                                    required
                                />
                            </div>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Llave</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={llave}
                                    onChange={(e) => setLlave(e.target.value)}
                                />
                                <span className='input-group-text'>OC</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="XXXX-XX"
                                    value={oc}
                                    onChange={(e) => setOc(e.target.value)}
                                />
                            </div>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Proveedor</span>
                                <select
                                    className="form-control"
                                    value={proveedor}
                                    onChange={(e) => setProveedor(e.target.value)}
                                    required
                                >
                                    <option value="">Seleccionar Proveedor</option>
                                    {proveedores?.map(prov => (
                                        <option key={prov.id_proveedor} value={prov.id_proveedor}>
                                            {prov.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Fecha Emisión</span>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={emision}
                                    onChange={(e) => setEmision(e.target.value)}
                                    required
                                />
                            </div>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Fecha Salida</span>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={salida}
                                    onChange={(e) => setSalida(e.target.value)}
                                />
                            </div>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Valor Productos</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={valorProductos}
                                    onChange={(e) => setValorProductos(e.target.value)}
                                />
                            </div>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Valor Total</span>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={valorTotal}
                                    onChange={(e) => setValorTotal(e.target.value)}
                                />
                            </div>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Paquetes</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={pkg}
                                    onChange={(e) => setPkg(e.target.value)}
                                />
                            </div>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Descuento</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={descuento}
                                    onChange={(e) => setDescuento(e.target.value)}
                                />
                            </div>
                            <div className='input-group mb-3'>
                                <span className='input-group-text'>Booking</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={booking}
                                    onChange={(e) => setBooking(e.target.value)}
                                />
                            </div>

                            {/* Sección de Productos mejorada similar a SubirArchivo */}
                            <div className='mt-4'>
                                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                                    <h5 className="mb-0">Productos ({detalles.length})</h5>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={handleAgregarDetalle}
                                    >
                                        <i className="bi bi-plus-circle me-1"></i> Agregar Producto
                                    </button>
                                </div>

                                {loadingDetalles ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" role="status" />
                                        <p className="mt-2">Cargando detalles...</p>
                                    </div>
                                ) : (
                                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <table className="table table-sm table-bordered align-middle">
                                            <thead className="table-light">
                                                <tr className="text-center">
                                                    <th>Producto / Grado</th>
                                                    <th style={{ width: '100px' }}>m3</th>
                                                    <th style={{ width: '100px' }}>USD/m3</th>
                                                    <th style={{ width: '120px' }}>Total</th>
                                                    <th style={{ width: '50px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detalles.map((p, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            <div className="d-flex flex-column gap-1">
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={p.producto || ''}
                                                                    onChange={(e) => {
                                                                        const selectedName = e.target.value;
                                                                        const prodObj = productNames.find(prod => prod.nombre_producto === selectedName);
                                                                        const updatedDetalles = [...detalles];
                                                                        updatedDetalles[idx] = { 
                                                                            ...updatedDetalles[idx], 
                                                                            producto: selectedName,
                                                                            id_producto: prodObj ? prodObj.id_producto : null
                                                                        };
                                                                        setDetalles(updatedDetalles);
                                                                    }}
                                                                >
                                                                    <option value="">Selecciona Producto...</option>
                                                                    {productNames.map((prod, pidx) => (
                                                                        <option key={pidx} value={prod.nombre_producto}>
                                                                            {prod.nombre_producto}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value={p.calidad || ''}
                                                                    onChange={(e) => handleDetalleChange(idx, 'calidad', e.target.value)}
                                                                >
                                                                    <option value="">Selecciona Grado...</option>
                                                                    {availableGrades.map((g, i) => (
                                                                        <option key={i} value={g.calidad}>{g.calidad}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.001"
                                                                className="form-control form-control-sm"
                                                                value={p.m3 || ''}
                                                                onChange={(e) => handleDetalleChange(idx, 'm3', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="form-control form-control-sm"
                                                                value={p.usd_m3 || ''}
                                                                onChange={(e) => handleDetalleChange(idx, 'usd_m3', e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="form-control form-control-sm"
                                                                value={p.total || ''}
                                                                onChange={(e) => handleDetalleChange(idx, 'total', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-link text-danger p-0"
                                                                onClick={() => handleDeleteDetalle(idx)}
                                                            >
                                                                <IoTrashOutline />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {detalles.length === 0 && (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-3 text-muted">
                                                            No hay productos registrados en esta Nota Fiscal.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='card-footer' style={{ textAlign: 'center' }}>
                            <button
                                type="submit"
                                className="btn btn-outline-success mb-3 mt-3"
                                disabled={botonDeshabilitado}
                            >
                                Guardar
                            </button>
                            <button type="button" className="btn btn-outline-danger" onClick={cerrar}>Cancelar</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default EditarNotaFiscal;
